'use client';

import { c as _c } from "react/compiler-runtime";
import { formatResolutionCountdown, remainingResolutionSeconds } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { PaintedMarketShareLeaf, usePaintedMarketShareDisplayStore } from '@/components/chart/painted-market-share-display';
import { OptimizedImage } from '@/components/images/optimized-image';
import { CardSectionLabel } from '@/components/ui/card-section-label';
import { Panel } from '@/components/ui/panel';
import { onEvent } from '@/events';
import { outcomeHueToStepVar } from '@/hooks/chart/use-outcome-color';
import { formatPercentValue } from '@/lib/chart/format';
import type { PaintedMarketShareSnapshot } from '@/lib/chart/painted-market-share-display';
import type { ResolutionCardOutcome, ResolutionCardState } from '@/lib/market/resolution-card-state';
import { MOTION_ENTRANCE, MOTION_EXIT } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
export type { ResolutionCardOutcome, ResolutionCardState };
type Direction = 'sooner' | 'later';
const MS_PER_SECOND: number = 1000;
const URGENT_SECONDS: number = 60;
/** After a trade the digits roll from the old time to the new one over this long. */
const ROLL_MS: number = 700;
/** The direction colour stays on the digits this long after the roll settles. */
const HOLD_MS: number = 900;
const THUMB_SIZE_CLASS: string = 'size-[46px]';
// Presence — the same spring and reduced-motion fallbacks as the Position
// card's quick-trade chips, so the card arrives and leaves the way they do.
const REVEAL_SPRING = {
  type: 'spring' as const,
  visualDuration: 0.12,
  bounce: 0.18
};
const REVEAL_SPRING_DELAYED = {
  type: 'spring' as const,
  visualDuration: 0.12,
  bounce: 0.18,
  delay: 0.05
};
const REVEAL_INITIAL = {
  opacity: 0
} as const;
const REVEAL_ANIMATE = {
  opacity: 1
} as const;
const REVEAL_EXIT = {
  opacity: 0,
  transition: REVEAL_SPRING
} as const;
const REVEAL_REDUCED = {
  duration: 0
} as const;
const REVEAL_EXIT_REDUCED = {
  opacity: 0
} as const;
// Trade delta readout beside the digits: slides in from the right with the
// roll and fades once the digits settle.
const DELTA_INITIAL = {
  opacity: 0,
  x: 4
} as const;
const DELTA_ANIMATE = {
  opacity: 1,
  x: 0
} as const;
const DELTA_EXIT = {
  opacity: 0,
  transition: MOTION_EXIT
} as const;
const DELTA_EXIT_REDUCED = {
  opacity: 0,
  transition: REVEAL_REDUCED
} as const;

/**
 * Resolution status card between the chart and the Outcomes strip, built
 * exactly like the Position card: a section label with a live dot, the 46px
 * outcome thumbnail with its hue dot, and one value row. Two states:
 *
 * - `projected`: the outcome on course to cross the threshold first, how far
 *   its share is from the line, and a countdown to the projected crossing.
 *   `crossAtMs` is fixed between trades (see `projectMarketResolutionAt`), so the digits
 *   only move for a trade — rolling up or down in the profit / loss colour —
 *   and the one-second tick lives in the countdown leaf alone.
 * - `resolving`: the outcome reached the line; the crank settles the market.
 * - `graduating`: the resolution landed; the page holds until the settled
 *   page's data is in. For both, the section label names the step and the
 *   countdown stays on the right, held at 0:00 in the warning tone, so the
 *   time never swaps out for something else.
 *
 * Pass `null` while there is nothing to show: the card stays mounted and
 * fades out through the same presence block the Position card's chips use
 * (`AnimatePresence initial={false}` — no animation on first paint, only
 * when the card appears or goes away; a projected → resolving change updates
 * the card in place so the countdown keeps running).
 */
export function ResolutionCard(t0) {
  const $ = _c(6);
  const {
    state,
    painted: t1
  } = t0;
  const painted = t1 === undefined ? false : t1;
  const prefersReduced = useReducedMotion() === true;
  let t2;
  if ($[0] !== painted || $[1] !== prefersReduced || $[2] !== state) {
    t2 = state !== null ? <m.div key="resolution-card" layout={true} transition={prefersReduced ? REVEAL_REDUCED : REVEAL_SPRING_DELAYED} initial={prefersReduced ? false : REVEAL_INITIAL} animate={REVEAL_ANIMATE} exit={prefersReduced ? REVEAL_EXIT_REDUCED : REVEAL_EXIT}><ResolutionCardBody state={state} painted={painted} /></m.div> : null;
    $[0] = painted;
    $[1] = prefersReduced;
    $[2] = state;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <LazyMotion features={domMax}><AnimatePresence initial={false} mode="popLayout">{t2}</AnimatePresence></LazyMotion>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function ResolutionCardBody(t0) {
  const $ = _c(54);
  const {
    state,
    painted
  } = t0;
  const t = useTranslations("chart.resolution_card");
  const tMarket = useTranslations("market");
  let t1;
  if ($[0] !== tMarket) {
    t1 = tMarket("backers.earnings_unavailable");
    $[0] = tMarket;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const unavailableText = t1;
  const {
    outcome
  } = state;
  const projectedCrossAtMs = state.kind === "projected" ? state.crossAtMs : null;
  let t2;
  if ($[2] !== state.gap || $[3] !== state.kind || $[4] !== state.threshold || $[5] !== t) {
    t2 = state.kind === "projected" ? t("from_resolution", {
      gap: formatPercentValue(state.gap, 2)
    }) : t("reached_line", {
      threshold: formatPercentValue(state.threshold, 0)
    });
    $[2] = state.gap;
    $[3] = state.kind;
    $[4] = state.threshold;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const serverText = t2;
  const firedRef = useRef(false);
  let t3;
  let t4;
  if ($[7] !== outcome.marketPubkey || $[8] !== outcome.outcomeIndex || $[9] !== projectedCrossAtMs) {
    t3 = () => {
      if (projectedCrossAtMs === null || firedRef.current) {
        return;
      }
      firedRef.current = true;
      onEvent("market_approaching_resolution_viewed", {
        marketPubkey: outcome.marketPubkey,
        outcomeIndex: outcome.outcomeIndex,
        secondsToResolve: remainingResolutionSeconds(projectedCrossAtMs, Date.now())
      });
    };
    t4 = [projectedCrossAtMs, outcome.marketPubkey, outcome.outcomeIndex];
    $[7] = outcome.marketPubkey;
    $[8] = outcome.outcomeIndex;
    $[9] = projectedCrossAtMs;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  const t5 = state.kind === "projected" ? "success" : "warning";
  let t6;
  if ($[12] !== t5) {
    t6 = <LiveDot tone={t5} />;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== state.kind || $[15] !== t) {
    t7 = state.kind === "projected" ? t("projected_head") : state.kind === "resolving" ? t("resolving") : t("graduating");
    $[14] = state.kind;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <CardSectionLabel className="px-4 pt-3 pb-2.5" afterTitle={t6}>{t7}</CardSectionLabel>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== outcome) {
    t9 = <OutcomeThumb outcome={outcome} />;
    $[20] = outcome;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== outcome.label) {
    t10 = <div className="truncate text-body-lg font-semibold tracking-tight text-foreground-primary">{outcome.label}</div>;
    $[22] = outcome.label;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== outcome.outcomeIndex || $[25] !== painted || $[26] !== serverText || $[27] !== state.kind || $[28] !== t || $[29] !== unavailableText) {
    t11 = painted ? <PaintedMarketShareLeaf fallback={serverText} format={snapshot => {
      const threshold = snapshot.threshold;
      const paintedValue = snapshot.values[outcome.outcomeIndex];
      if (threshold === null) {
        return {
          text: unavailableText
        };
      }
      if (state.kind !== "projected") {
        return {
          text: t("reached_line", {
            threshold: formatPercentValue(threshold, 0)
          })
        };
      }
      if (typeof paintedValue !== "number" || !Number.isFinite(paintedValue)) {
        return {
          text: unavailableText
        };
      }
      return {
        text: t("from_resolution", {
          gap: formatPercentValue(Math.max(0, threshold - paintedValue), 2)
        })
      };
    }} /> : serverText;
    $[24] = outcome.outcomeIndex;
    $[25] = painted;
    $[26] = serverText;
    $[27] = state.kind;
    $[28] = t;
    $[29] = unavailableText;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11) {
    t12 = <div className="mt-0.5 text-body text-foreground-tertiary leading-tight tabular-nums">{t11}</div>;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t10 || $[34] !== t12) {
    t13 = <div className="min-w-0">{t10}{t12}</div>;
    $[33] = t10;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t) {
    t14 = t("resolves_in");
    $[36] = t;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14) {
    t15 = <div className="text-body text-foreground-tertiary leading-tight">{t14}</div>;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== painted || $[41] !== state.crossAtMs || $[42] !== state.kind) {
    t16 = painted ? <PaintedResolutionCountdown crossAtMs={state.crossAtMs} settled={state.kind !== "projected"} /> : <ResolutionCountdown crossAtMs={state.crossAtMs} settled={state.kind !== "projected"} />;
    $[40] = painted;
    $[41] = state.crossAtMs;
    $[42] = state.kind;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t15 || $[45] !== t16) {
    t17 = <div className="min-w-0 text-right">{t15}{t16}</div>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t13 || $[48] !== t17 || $[49] !== t9) {
    t18 = <div className="border-t border-border-primary px-4 pt-3.5 pb-4"><div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3.5">{t9}{t13}{t17}</div></div>;
    $[47] = t13;
    $[48] = t17;
    $[49] = t9;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== t18 || $[52] !== t8) {
    t19 = <Panel className="flex flex-col gap-0 overflow-hidden p-0">{t8}{t18}</Panel>;
    $[51] = t18;
    $[52] = t8;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  return t19;
}
function PaintedResolutionCountdown(t0) {
  const $ = _c(10);
  const {
    crossAtMs,
    settled
  } = t0;
  const store = usePaintedMarketShareDisplayStore();
  let t1;
  if ($[0] !== store) {
    t1 = () => store.getPaintSnapshot()?.displayedHeadTimeMs ?? Date.now();
    $[0] = store;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [paintedNowMs, setPaintedNowMs] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== crossAtMs || $[3] !== store) {
    t2 = () => store.subscribePaint(() => {
      const headMs = store.getPaintSnapshot()?.displayedHeadTimeMs;
      if (headMs === undefined) {
        return;
      }
      setPaintedNowMs(current => remainingResolutionSeconds(crossAtMs, current) === remainingResolutionSeconds(crossAtMs, headMs) ? current : headMs);
    });
    t3 = [store, crossAtMs];
    $[2] = crossAtMs;
    $[3] = store;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== crossAtMs || $[7] !== paintedNowMs || $[8] !== settled) {
    t4 = <ResolutionCountdown crossAtMs={crossAtMs} settled={settled} paintedNowMs={paintedNowMs} />;
    $[6] = crossAtMs;
    $[7] = paintedNowMs;
    $[8] = settled;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function LiveDot(t0) {
  const $ = _c(11);
  const {
    tone
  } = t0;
  const fill = tone === "success" ? "bg-feedback-success-accent" : "bg-feedback-warning-accent";
  let t1;
  if ($[0] !== fill) {
    t1 = cn("absolute inset-0 animate-ping rounded-full opacity-60 motion-reduce:animate-none", fill);
    $[0] = fill;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== fill) {
    t3 = cn("absolute inset-0 m-auto size-1.5 rounded-full", fill);
    $[4] = fill;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className={t3} />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <span aria-hidden={true} className="relative inline-flex size-2">{t2}{t4}</span>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function OutcomeThumb(t0) {
  const $ = _c(18);
  const {
    outcome
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("relative shrink-0", THUMB_SIZE_CLASS);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== outcome.hue || $[2] !== outcome.imageNsfw || $[3] !== outcome.imageUrl || $[4] !== outcome.marketPubkey || $[5] !== outcome.outcomeIndex) {
    t2 = {
      kind: "outcome",
      marketPubkey: outcome.marketPubkey,
      outcomeIndex: outcome.outcomeIndex,
      hue: outcome.hue,
      imageUrl: outcome.imageUrl,
      nsfw: outcome.imageNsfw
    };
    $[1] = outcome.hue;
    $[2] = outcome.imageNsfw;
    $[3] = outcome.imageUrl;
    $[4] = outcome.marketPubkey;
    $[5] = outcome.outcomeIndex;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== outcome.label || $[8] !== t2) {
    t3 = <OptimizedImage variant="thumbnail" entity={t2} alt={outcome.label} size={THUMB_SIZE_CLASS} rounded="rounded-control" preview={false} />;
    $[7] = outcome.label;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("absolute -right-1 -bottom-1 size-[13px] rounded-full border-2 border-background-primary");
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== outcome.hue) {
    t5 = outcomeHueToStepVar(outcome.hue, 400);
    $[11] = outcome.hue;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <span aria-hidden={true} data-testid="resolution-hue-dot" className={t4} style={{
      backgroundColor: t5
    }} />;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t6) {
    t7 = <div className={t1}>{t3}{t6}</div>;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
interface TradeMove {
  /** Presence key — one readout per burst of trades, updated in place. */
  readonly key: number;
  readonly direction: Direction;
  /** Net signed change to the remaining time across the burst, in seconds. */
  readonly deltaSeconds: number;
}

/** The only part of the card that ticks. */
function ResolutionCountdown({
  crossAtMs,
  settled,
  paintedNowMs
}: {
  crossAtMs: number;
  /** The line has been reached: hold at 0:00 and ignore crossing-time changes. */
  settled: boolean;
  paintedNowMs?: number;
}): React.ReactNode {
  const t = useTranslations('chart.resolution_card');
  const prefersReduced = useReducedMotion() === true;
  const [localNowMs, setLocalNowMs] = useState(() => Date.now());
  const nowMs = paintedNowMs ?? localNowMs;
  const nowRef = useRef(nowMs);
  const [rolled, setRolled] = useState<number | null>(null);
  const [trade, setTrade] = useState<TradeMove | null>(null);
  const shownRef = useRef<number | null>(null);
  const crossAtRef = useRef(crossAtMs);
  const seconds = rolled ?? remainingResolutionSeconds(crossAtMs, nowMs);
  useEffect(() => {
    if (paintedNowMs !== undefined) return;
    const id = setInterval(() => setLocalNowMs(Date.now()), MS_PER_SECOND);
    return (): void => clearInterval(id);
  }, [paintedNowMs]);
  useEffect(() => {
    nowRef.current = nowMs;
  }, [nowMs]);

  // A trade moved the crossing time: show how much, roll the digits from the
  // last painted value to the new one, colour both by direction, then settle.
  // A burst of trades folds into the one readout (net delta, same element, so
  // nothing re-mounts) and each new roll takes over from wherever the digits
  // are — the previous rolled value stays on screen until the next frame
  // overwrites it, so the target never flashes in between.
  useEffect(() => {
    if (crossAtRef.current === crossAtMs) return;
    const deltaSeconds = Math.round((crossAtMs - crossAtRef.current) / MS_PER_SECOND);
    crossAtRef.current = crossAtMs;
    if (settled) {
      // Re-running this effect cancelled the timer that clears the readout.
      setRolled(null);
      setTrade(null);
      return;
    }
    setTrade((current: TradeMove | null): TradeMove | null => {
      const total = (current?.deltaSeconds ?? 0) + deltaSeconds;
      if (total === 0) return null;
      return {
        key: current?.key ?? crossAtMs,
        direction: total < 0 ? 'sooner' : 'later',
        deltaSeconds: total
      };
    });
    const hold = setTimeout(() => setTrade(null), ROLL_MS + HOLD_MS);
    const from = shownRef.current;
    if (prefersReduced || from === null) {
      setRolled(null);
      return (): void => clearTimeout(hold);
    }
    const startedAt = performance.now();
    let frame = requestAnimationFrame(function step(now: number): void {
      const progress = Math.min(1, (now - startedAt) / ROLL_MS);
      const target = remainingResolutionSeconds(crossAtMs, nowRef.current);
      setRolled(Math.round(from + (target - from) * easeOut(progress)));
      if (progress < 1) frame = requestAnimationFrame(step);else setRolled(null);
    });
    return (): void => {
      clearTimeout(hold);
      cancelAnimationFrame(frame);
    };
  }, [crossAtMs, settled, prefersReduced]);

  // The last painted value is where the next trade's roll starts from. This
  // must run AFTER the trade effect above: effects fire in declaration order,
  // and the trade effect needs the value painted before the crossing time
  // changed, not the one just computed from the new target.
  useEffect(() => {
    shownRef.current = seconds;
  });
  const clock = (total_0: number): string => {
    const formatted = formatResolutionCountdown(total_0);
    switch (formatted.kind) {
      case 'days':
        return t('countdown_days', {
          days: formatted.days,
          hours: formatted.hours
        });
      case 'clock':
        return formatted.text;
      default:
        {
          const exhaustive: never = formatted;
          return exhaustive;
        }
    }
  };
  const direction = trade?.direction ?? null;
  const urgent = seconds <= URGENT_SECONDS;
  const tone = direction === 'sooner' ? 'text-data-profit' : direction === 'later' ? 'text-data-loss' : urgent ? 'text-feedback-warning-content' : 'text-foreground-primary';

  // The server and the client paint a different second; React reconciles the
  // text without a hydration warning.
  return <div className="relative mt-0.5 inline-block">
      <AnimatePresence initial={false}>
        {trade !== null ? <m.span key={trade.key} data-testid="resolution-delta" aria-hidden initial={prefersReduced ? false : DELTA_INITIAL} animate={DELTA_ANIMATE} exit={prefersReduced ? DELTA_EXIT_REDUCED : DELTA_EXIT} transition={prefersReduced ? REVEAL_REDUCED : MOTION_ENTRANCE} className={cn('absolute top-1/2 right-full mr-1.5 -translate-y-1/2 whitespace-nowrap text-caption font-semibold tabular-nums', trade.direction === 'sooner' ? 'text-data-profit' : 'text-data-loss')}>
            {`${trade.deltaSeconds < 0 ? '−' : '+'}${clock(Math.abs(trade.deltaSeconds))}`}
          </m.span> : null}
      </AnimatePresence>
      <div data-testid="resolution-countdown" suppressHydrationWarning className={cn('text-heading-lg font-bold tabular-nums transition-colors', tone, urgent && direction === null && 'animate-pulse motion-reduce:animate-none')}>
        {clock(seconds)}
      </div>
    </div>;
}
function easeOut(progress: number): number {
  return 1 - (1 - progress) ** 3;
}